import { Loading } from '@/components/Loading'
import RichText from '@/components/RichText'
import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import PrescriptionCourseAccordion from '@/components/prescriptions/PrescriptionCourseAccordion'
import PrescriptionDownloadSelectionButton from '@/components/prescriptions/PrescriptionDownloadSelectionButton'
import PrescriptionElectiveAccordion from '@/components/prescriptions/PrescriptionElectiveAccordion'
import PrescriptionFocusAreaAccordion from '@/components/prescriptions/PrescriptionFocusAreaAccordion'
import PrescriptionStudiesAccordion from '@/components/prescriptions/PrescriptionStudiesAccordion'
import { buildPrescriptionPrintPayload } from '@/components/prescriptions/utils/buildPrescriptionPrintPayload'
import {
	buildGACourse,
	getGAPageContext,
	PRESCRIPTION_GA_EVENT,
} from '@/components/prescriptions/utils/prescriptionGAEvents'
import {
	courseCodenameMatchesDeepLink,
	courseMatchesDeepLink,
	CourseStatus,
	ElectiveSelectionState,
	formatPrescriptionsListLabel,
	getCourseStatus,
	isElectiveCourse,
	isStudiesVariant,
	SELECTION_FETCH_ERROR_MESSAGE,
	SelectionState,
	sortCoursesByDisplayOrder,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import { sendPrescriptionGAEvent } from '@/components/prescriptions/utils/sendPrescriptionGAEvent'
import { fetchPrescriptionSelection } from '@/fetchers/fetchPrescription'
import type { Mapping } from '@/types'
import type { PrescriptionSelectionCourse } from '@/types/fetchPrescriptionSelection'
import { isRichtextElementEmpty } from '@/utils'
import type { Elements, IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { PrescriptionInPageAlert } from './PrescriptionStatusAlert'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface PrescriptionSelectionToolProps {
	tabContent?: Elements.RichTextElement
	mappings?: Mapping[]
	linkedItems?: IContentItemsContainer
	listcodename?: string
}

export function PrescriptionSelectionTool({
	tabContent,
	mappings = [],
	linkedItems = {},
	listcodename = '',
}: PrescriptionSelectionToolProps) {
	const router = useRouter()
	const { preview } = useKontentHomeConfig()
	const showTabContent = tabContent && !isRichtextElementEmpty(tabContent)

	const { data, isFetching, isError } = useQuery({
		queryKey: ['prescription-selection', listcodename, preview],
		queryFn: () => fetchPrescriptionSelection({ listcodename, preview }),
		enabled: Boolean(listcodename),
		refetchOnWindowFocus: false,
	})

	const selectionData = data?.json?.data
	const listMeta = selectionData?.['prescriptions-list']
	const courses = useMemo(
		() => sortCoursesByDisplayOrder(selectionData?.courses ?? []),
		[selectionData],
	)

	const [selectionState, setSelectionState] = useState<SelectionState>({})
	const [electiveState, setElectiveState] = useState<ElectiveSelectionState>(
		{},
	)

	useEffect(() => {
		setSelectionState({})
		setElectiveState({})
	}, [listcodename])

	const [openCourses, setOpenCourses] = useState<Record<string, boolean>>({})

	const toggleCourseOpen = useCallback((courseTitle: string) => {
		setSelectionState((prev) => prev) // no-op to keep stable refs
		setOpenCourses((prev) => ({
			...prev,
			[courseTitle]: !prev[courseTitle],
		}))
	}, [])

	const deepLinkRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
	const setAccordionRef = useCallback(
		(courseTitle: string) =>
			(node: HTMLDivElement | null): void => {
				deepLinkRefs.current.set(courseTitle, node)
			},
		[],
	)
	const hasAppliedDeepLinkRef = useRef(false)

	useEffect(() => {
		if (hasAppliedDeepLinkRef.current) return
		if (courses.length === 0) return

		const queryValue = router.query.course
		const courseParam = Array.isArray(queryValue)
			? queryValue[0]
			: queryValue
		if (!courseParam) {
			hasAppliedDeepLinkRef.current = true
			return
		}

		hasAppliedDeepLinkRef.current = true

		const nextQuery = { ...router.query }
		delete nextQuery.course
		router.replace(
			{ pathname: router.pathname, query: nextQuery },
			undefined,
			{ shallow: true, scroll: false },
		)

		const match = courses.find(
			(c) =>
				courseCodenameMatchesDeepLink(
					c['course-codename'],
					courseParam,
				) || courseMatchesDeepLink(c['course-title'], courseParam),
		)
		if (!match) {
			return
		}

		setOpenCourses({ [match['course-title']]: true })

		// One-time scroll into view after layout is committed.
		const id = window.setTimeout(() => {
			const node = deepLinkRefs.current.get(match['course-title'])
			if (node)
				node.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}, 0)
		return () => window.clearTimeout(id)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courses, router.query.course])

	const previousStatusesRef = useRef<Record<string, CourseStatus>>({})

	const courseStatuses = useMemo<Record<string, CourseStatus>>(() => {
		const map: Record<string, CourseStatus> = {}
		courses.forEach((course) => {
			const title = course['course-title']
			map[title] = getCourseStatus(
				course,
				selectionState[title],
				electiveState[title],
			)
		})
		return map
	}, [courses, selectionState, electiveState])

	useEffect(() => {
		const previous = previousStatusesRef.current
		const prescriptionsListLabel = formatPrescriptionsListLabel(
			listMeta?.title,
			listMeta?.years,
		)

		Object.entries(courseStatuses).forEach(([courseTitle, status]) => {
			const wasComplete = previous[courseTitle] === 'complete'
			if (status !== 'complete' || wasComplete) return

			const course = courses.find(
				(c) => c['course-title'] === courseTitle,
			)
			if (!course) return

			sendPrescriptionGAEvent({
				event: PRESCRIPTION_GA_EVENT.tool1CourseCompletion,
				...getGAPageContext(),
				prescriptions_list: prescriptionsListLabel,
				course: buildGACourse(
					course,
					selectionState[courseTitle],
					electiveState[courseTitle],
				),
			})
		})

		previousStatusesRef.current = { ...courseStatuses }
	}, [courseStatuses, courses, listMeta, selectionState, electiveState])

	const handleSelectionChange = useCallback(
		(courseTitle: string, slotIndex: number, workIds: string[]) => {
			setSelectionState((prev) => {
				const next: SelectionState = { ...prev }
				const courseSelections = { ...(next[courseTitle] ?? {}) }
				if (workIds.length === 0) {
					delete courseSelections[slotIndex]
				} else {
					courseSelections[slotIndex] = { workIds }
				}
				if (Object.keys(courseSelections).length === 0) {
					delete next[courseTitle]
				} else {
					next[courseTitle] = courseSelections
				}
				return next
			})
		},
		[],
	)

	const handleResetCourse = useCallback((courseTitle: string) => {
		setSelectionState((prev) => {
			if (!prev[courseTitle]) return prev
			const next: SelectionState = { ...prev }
			delete next[courseTitle]
			return next
		})
		setElectiveState((prev) => {
			if (prev[courseTitle] === undefined) return prev
			const next: ElectiveSelectionState = { ...prev }
			delete next[courseTitle]
			return next
		})
	}, [])

	const handleResetAll = useCallback(() => {
		setSelectionState({})
		setElectiveState({})
	}, [])

	const handleElectiveChange = useCallback(
		(courseTitle: string, newElectiveIndex: number | undefined) => {
			setElectiveState((prev) => {
				if (prev[courseTitle] === newElectiveIndex) return prev
				const next: ElectiveSelectionState = { ...prev }
				if (newElectiveIndex === undefined) {
					delete next[courseTitle]
				} else {
					next[courseTitle] = newElectiveIndex
				}
				return next
			})
			setSelectionState((prev) => {
				if (!prev[courseTitle]) return prev
				const next: SelectionState = { ...prev }
				delete next[courseTitle]
				return next
			})
		},
		[],
	)

	const completeCourses = useMemo(
		() =>
			courses.filter(
				(c) => courseStatuses[c['course-title']] === 'complete',
			),
		[courses, courseStatuses],
	)

	// Nested GA payload for the download event — one entry per complete course,
	// each with its selections (GTM splits into per-selection events on download).
	const gaDownloadCourses = useMemo(
		() =>
			completeCourses.map((course) =>
				buildGACourse(
					course,
					selectionState[course['course-title']],
					electiveState[course['course-title']],
				),
			),
		[completeCourses, selectionState, electiveState],
	)

	const isDownloadDisabled = useMemo(() => {
		if (completeCourses.length === 0) return true
		return courses.some(
			(course) => courseStatuses[course['course-title']] === 'incomplete',
		)
	}, [courses, courseStatuses, completeCourses])

	const printPayload = useMemo(
		() =>
			buildPrescriptionPrintPayload(
				completeCourses,
				selectionState,
				electiveState,
				listcodename,
				false,
			),
		[completeCourses, selectionState, electiveState, listcodename],
	)

	const showAccordions = !isError && !(isFetching && courses.length === 0)
	const showLoading = isFetching && courses.length === 0
	const showError = isError

	return (
		<div className="p-4">
			{showTabContent && (
				<div className="mb-6 text-nsw-grey-01">
					<RichText
						richTextElement={tabContent}
						linkedItems={linkedItems}
						mappings={mappings}
					/>
				</div>
			)}

			{showLoading && (
				<div className="bg-white/90 w-full flex justify-center pt-6">
					<Loading />
				</div>
			)}

			{showError && (
				<PrescriptionInPageAlert
					as="warning"
					title="Couldn't load selections"
				>
					{SELECTION_FETCH_ERROR_MESSAGE}
				</PrescriptionInPageAlert>
			)}

			{showAccordions && courses.length > 0 && (
				<div data-testid="prescription-selection-accordions">
					{courses.map((course) => {
						const courseTitle = course['course-title']
						const courseSelections = selectionState[courseTitle]
						const status = courseStatuses[courseTitle] ?? 'optional'
						const isOpen = Boolean(openCourses[courseTitle])
						const isStudies = isStudiesVariant(course)
						const isElective = isElectiveCourse(course)
						return (
							<PrescriptionCourseAccordion
								key={courseTitle}
								ref={setAccordionRef(courseTitle)}
								course={course}
								status={status}
								isOpen={isOpen}
								onToggle={() => toggleCourseOpen(courseTitle)}
							>
								{isStudies ? (
									<PrescriptionStudiesAccordion
										course={course}
										courseSelections={courseSelections}
										onSelectionChange={(slot, ids) =>
											handleSelectionChange(
												courseTitle,
												slot,
												ids,
											)
										}
										onResetCourse={() =>
											handleResetCourse(courseTitle)
										}
									/>
								) : isElective ? (
									<PrescriptionElectiveAccordion
										course={course}
										courseSelections={courseSelections}
										electiveOptionIndex={
											electiveState[courseTitle]
										}
										onElectiveChange={(newIndex) =>
											handleElectiveChange(
												courseTitle,
												newIndex,
											)
										}
										onSelectionChange={(slot, ids) =>
											handleSelectionChange(
												courseTitle,
												slot,
												ids,
											)
										}
										onResetCourse={() =>
											handleResetCourse(courseTitle)
										}
									/>
								) : (
									<PrescriptionFocusAreaAccordion
										course={course}
										courseSelections={courseSelections}
										onSelectionChange={(slot, ids) =>
											handleSelectionChange(
												courseTitle,
												slot,
												ids,
											)
										}
										onResetCourse={() =>
											handleResetCourse(courseTitle)
										}
									/>
								)}
							</PrescriptionCourseAccordion>
						)
					})}
				</div>
			)}

			{showAccordions && courses.length > 0 && (
				<div className="flex justify-end items-center gap-4 mt-6">
					<button
						type="button"
						className="underline font-bold nsw-text--brand-dark"
						onClick={handleResetAll}
						data-testid="prescription-reset-all"
					>
						Reset all selections
					</button>
					<PrescriptionDownloadSelectionButton
						listcodename={listcodename}
						listMeta={listMeta}
						gaCourses={gaDownloadCourses}
						printPayload={printPayload}
						disabled={isDownloadDisabled}
						preview={preview}
					/>
				</div>
			)}
		</div>
	)
}

export default PrescriptionSelectionTool

export type { PrescriptionSelectionCourse }
